import RGL from 'react-grid-layout';

import { DataPoolName } from 'dashboards/types/data_pool';
import { DataWidgetFiltersInput } from 'dashboards/types/filter';
import { DimensionInput } from 'dashboards/types/dimension';
import { Sort } from 'types/column';
import { TimeSeriesGranularity } from 'dashboards/types/time';
import { UseFilesParams } from 'files/hooks/useFiles';
import { UseFilesSearchParams } from 'files/hooks/useFilesSearch';
import { UseStreamsParams } from 'streams/hooks/useStreams';

export type WidgetType =
  | 'data'
  | 'iframe'
  | 'stream'
  | 'multistream'
  | 'files'
  | 'files-search';
export type ChartType =
  | 'timeseries-bar'
  | 'timeseries-line'
  | 'timeseries-bar-stacked'
  | 'timeseries-bar-grouped'
  | 'timeseries-heatmap'
  | 'pie'
  | 'leaderboard-bar'
  | 'leaderboard-table'
  | 'table'
  | 'counter';

// Selected chart props that are persisted with api-server
type DataWidgetQuery<T extends ChartType> = T extends
  | 'timeseries-bar'
  | 'timeseries-line'
  ? {
      metric: string;
      granularity: TimeSeriesGranularity;
      filters?: DataWidgetFiltersInput[];
    }
  : T extends
        | 'timeseries-bar-stacked'
        | 'timeseries-bar-grouped'
        | 'timeseries-heatmap'
    ? {
        metric: string;
        dimensions: DimensionInput[];
        granularity: TimeSeriesGranularity;
        filters?: DataWidgetFiltersInput[];
      }
    : T extends 'pie' | 'leaderboard-bar' | 'leaderboard-table'
      ? {
          metric: string;
          dimensions: DimensionInput[];
          rowLimit: number;
          sort: Sort;
          filters?: DataWidgetFiltersInput[];
        }
      : T extends 'table'
        ? {
            dataPool: { name: DataPoolName };
            columns: string[];
            orderByColumn: string;
            sort: Sort;
            filters?: DataWidgetFiltersInput[];
          }
        : T extends 'counter'
          ? {
              metric: string;
              filters?: DataWidgetFiltersInput[];
            }
          : never;

export abstract class DashboardWidget<Type extends WidgetType = WidgetType> {
  id: string;

  name: string;

  type: Type;

  layout: RGL.Layout;

  protected constructor(
    id: string,
    name: string,
    type: Type,
    layout: Omit<RGL.Layout, 'i'>
  ) {
    this.id = id;
    this.name = name;
    this.type = type;
    this.layout = {
      i: id,
      ...layout,
    };
  }
}

export class DataWidget<T extends ChartType> extends DashboardWidget<'data'> {
  chartType: T;
  query: DataWidgetQuery<T>;

  constructor(
    id: string,
    name: string,
    chartType: T,
    query: DataWidgetQuery<T>,
    layout: Omit<RGL.Layout, 'i'>
  ) {
    super(id, name, 'data', layout);
    this.chartType = chartType;
    this.query = query;
  }
}

export class IframeWidget extends DashboardWidget<'iframe'> {
  /** Iframe URL */
  url: string;

  constructor(
    id: string,
    name: string,
    url: string,
    layout: Omit<RGL.Layout, 'i'>
  ) {
    super(id, name, 'iframe', layout);
    this.url = url;
  }
}

export class StreamWidget extends DashboardWidget<'stream'> {
  streamId: string;
  autoPlay: boolean;

  constructor(
    id: string,
    name: string,
    streamId: string,
    autoPlay: boolean,
    layout: Omit<RGL.Layout, 'i'>
  ) {
    super(id, name, 'stream', layout);
    this.streamId = streamId;
    this.autoPlay = autoPlay;
  }
}

export class MultiStreamWidget extends DashboardWidget<'multistream'> {
  options: UseStreamsParams;
  autoPlay: boolean;

  constructor(
    id: string,
    name: string,
    options: UseStreamsParams,
    autoPlay: boolean,
    layout: Omit<RGL.Layout, 'i'>
  ) {
    super(id, name, 'multistream', layout);
    this.options = options;
    this.autoPlay = autoPlay;
  }
}

export class FilesWidget extends DashboardWidget<'files'> {
  options: UseFilesParams;

  constructor(
    id: string,
    name: string,
    options: UseFilesParams,
    layout: Omit<RGL.Layout, 'i'>
  ) {
    super(id, name, 'files', layout);
    this.options = options;
  }
}

export class FilesSearchWidget extends DashboardWidget<'files-search'> {
  options: Required<
    Pick<
      UseFilesSearchParams,
      | 'query'
      | 'search_entities'
      | 'confidence'
      | 'sort_by'
      | 'camera_ids'
      | 'stream_ids'
      | 'gateway_ids'
    >
  >;

  constructor(
    id: string,
    name: string,
    options: FilesSearchWidget['options'],
    layout: Omit<RGL.Layout, 'i'>
  ) {
    super(id, name, 'files-search', layout);
    this.options = options;
  }
}

// Type Guards for chart types
export function isDataWidgetTimeseriesBar(
  widget: DataWidget<ChartType>
): widget is DataWidget<'timeseries-bar'> {
  return widget.chartType === 'timeseries-bar';
}

export function isDataWidgetTimeseriesLine(
  widget: DataWidget<ChartType>
): widget is DataWidget<'timeseries-line'> {
  return widget.chartType === 'timeseries-line';
}

export function isDataWidgetTimeseriesBarStacked(
  widget: DataWidget<ChartType>
): widget is DataWidget<'timeseries-bar-stacked'> {
  return widget.chartType === 'timeseries-bar-stacked';
}

export function isDataWidgetTimeseriesBarGrouped(
  widget: DataWidget<ChartType>
): widget is DataWidget<'timeseries-bar-grouped'> {
  return widget.chartType === 'timeseries-bar-grouped';
}

export function isDataWidgetTimeseriesHeatmap(
  widget: DataWidget<ChartType>
): widget is DataWidget<'timeseries-heatmap'> {
  return widget.chartType === 'timeseries-heatmap';
}

export function isDataWidgetPie(
  widget: DataWidget<ChartType>
): widget is DataWidget<'pie'> {
  return widget.chartType === 'pie';
}

export function isDataWidgetLeaderboardBar(
  widget: DataWidget<ChartType>
): widget is DataWidget<'leaderboard-bar'> {
  return widget.chartType === 'leaderboard-bar';
}

export function isDataWidgetLeaderboardTable(
  widget: DataWidget<ChartType>
): widget is DataWidget<'leaderboard-table'> {
  return widget.chartType === 'leaderboard-table';
}

export function isDataWidgetTable(
  widget: DataWidget<ChartType>
): widget is DataWidget<'table'> {
  return widget.chartType === 'table';
}

export function isDataWidgetCounter(
  widget: DataWidget<ChartType>
): widget is DataWidget<'counter'> {
  return widget.chartType === 'counter';
}

// Type Guards for WidgetType
export function isDataWidget(
  widget: DashboardWidget
): widget is DataWidget<ChartType> {
  return widget.type === 'data';
}

export function isIframeWidget(
  widget: DashboardWidget
): widget is IframeWidget {
  return widget.type === 'iframe';
}

export function isStreamWidget(
  widget: DashboardWidget
): widget is StreamWidget {
  return widget.type === 'stream';
}

export function isMultiStreamWidget(
  widget: DashboardWidget
): widget is MultiStreamWidget {
  return widget.type === 'multistream';
}

export function isFilesWidget(widget: DashboardWidget): widget is FilesWidget {
  return widget.type === 'files';
}

export function isFilesSearchWidget(
  widget: DashboardWidget
): widget is FilesSearchWidget {
  return widget.type === 'files-search';
}
